import { UserPlusIcon } from 'lucide-react';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useCurrentStateAndParams } from '@uirouter/react';

import { useIsRBACEnabled } from '@/react/kubernetes/cluster/useIsRBACEnabled';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { RBACAlert } from '@/react/kubernetes/cluster/ConfigureView/ConfigureForm/RBACAlert';
import { useUsers } from '@/portainer/users/queries/useUsers';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';

import { Widget, WidgetBody, WidgetTitle } from '@@/Widget';
import { TextTip } from '@@/Tip/TextTip';

import { EnvironmentAccess } from '../types';
import { parseNamespaceAccessPolicy } from '../parseNamespaceAccessPolicy';
import { useNamespaceAccessPolicy } from '../useNamespaceAccessPolicy';
import { useUpdateNamespaceAccessesMutation } from '../useUpdateNamespaceAccessesMutation';
import { createAccessesPayload } from '../createAccessesPayload';

import { CreateAccessValues } from './types';
import { CreateAccessInnerForm } from './CreateAccessInnerForm';
import { validationSchema } from './createAccess.validation';

export function CreateAccessWidget() {
  const {
    params: { id: namespaceName },
  } = useCurrentStateAndParams();
  const environmentId = useEnvironmentId();
  const isRBACEnabledQuery = useIsRBACEnabled(environmentId);
  const initialValues: {
    selectedUsersAndTeams: EnvironmentAccess[];
  } = {
    selectedUsersAndTeams: [],
  };
  const usersQuery = useUsers(false, environmentId, !!environmentId);
  const teamsQuery = useTeams(false, environmentId, {
    enabled: !!environmentId,
  });
  const accessPolicyQuery = useNamespaceAccessPolicy(
    environmentId,
    namespaceName
  );

  const namespaceAccesses = useMemo(
    () =>
      parseNamespaceAccessPolicy(
        accessPolicyQuery.data,
        usersQuery.data ?? [],
        teamsQuery.data ?? []
      ),
    [accessPolicyQuery.data, usersQuery.data, teamsQuery.data]
  );

  const updateNamespaceAccessMutation = useUpdateNamespaceAccessesMutation(
    environmentId,
    namespaceName
  );

  return (
    <div className="row">
      <div className="col-sm-12">
        <Widget aria-label="Create access">
          <WidgetTitle icon={UserPlusIcon} title="Create access" />
          <WidgetBody>
            {isRBACEnabledQuery.data === false && <RBACAlert />}
            <TextTip className="mb-2" childrenWrapperClassName="text-warning">
              Adding user access will require the affected user(s) to logout and
              login for the changes to be taken into account.
            </TextTip>
            {isRBACEnabledQuery.data !== false && (
              <Formik<CreateAccessValues>
                initialValues={initialValues}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                validateOnMount
              >
                {(formikProps) => (
                  <CreateAccessInnerForm
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...formikProps}
                    namespaceAccessesGranted={namespaceAccesses}
                  />
                )}
              </Formik>
            )}
          </WidgetBody>
        </Widget>
      </div>
    </div>
  );

  async function onSubmit(
    values: {
      selectedUsersAndTeams: EnvironmentAccess[];
    },
    { resetForm }: { resetForm: () => void }
  ) {
    try {
      const accessesPayload = createAccessesPayload(
        values.selectedUsersAndTeams,
        []
      );
      await updateNamespaceAccessMutation.mutateAsync(accessesPayload);
      notifySuccess('Success', 'Namespace access updated');
      resetForm();
    } catch (error) {
      notifyError('Failed to update namespace access', error as Error);
    }
  }
}
